export const en = {
    common: {
        home: 'Home',
        search: 'Search',
        guest: 'Guest',
        logout: 'Log out',
        toggleTheme: 'Toggle theme',
        toggleSidebar: 'Toggle Sidebar',
        close: 'Close',
        more: 'More',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        delete: 'Delete',
        export: 'Export',
        loading: 'Loading...',
        activity: 'Activity',
        confirm: 'Confirm',
        noResultsFound: 'No results found.',
        justNow: 'just now',
        minutesAgo: '{{count}}m ago',
        hoursAgo: '{{count}}h ago',
        daysAgo: '{{count}}d ago',
        pagination: {
            previous: 'Previous',
            next: 'Next',
            page: 'Page {{page}} of {{totalPages}}',
            summary: 'Page {{page}} of {{totalPages}} · showing {{count}} of {{total}} items'
        }
    },
    notFound: {
        title: '404',
        heading: 'Page not found',
        description: 'The page you are looking for does not exist or has been moved.',
        cta: 'Go to Dashboard'
    },
    routeError: {
        heading: 'Something went wrong',
        description: 'The router hit an unexpected error while loading this page.',
        cta: 'Back to Organizations'
    },
    startup: {
        preparingSessionTitle: 'Preparing your session',
        preparingSessionDescription: 'We are validating authentication before the app becomes interactive.',
        preparingSessionStatus: 'Checking authentication',
        loadingDataTitle: 'Loading startup data',
        loadingDataDescription: 'Fetching the required account and organization context for the app.',
        loadingDataStatus: 'Loading startup data',
        waitHint:
            'This gate stays visible until startup is fully ready so protected navigation does not flash in early.',
        errorTitle: 'We could not finish startup',
        errorDescription: 'A recoverable startup request failed. Retry to continue without a full page refresh.',
        errorFallback: 'Unexpected startup failure.',
        retry: 'Retry startup',
        retryHint: 'If the problem persists, check the backend connectivity and authentication status.'
    },
    appShell: {
        onboardingCheck: {
            failureTitle: 'We could not verify onboarding yet',
            failureDescription: 'Workspace actions stay paused while we retry the organization onboarding check.',
            retry: 'Retry onboarding check'
        }
    },
    dashboardWelcome: {
        badge: 'First-run dashboard welcome',
        title: 'You’re in. Here’s your post-checkout scaffold.',
        description:
            'FE-only mocked dashboard state for new organizations. Real API key creation, starter templates, and live console output land later.',
        continueAction: 'Continue to organization',
        apiKeyTitle: 'API key scaffold',
        apiKeyDescription: 'Placeholder card for the first project key, scopes, copy action, and last-used metadata.',
        primaryKey: 'Primary key',
        createApiKey: 'Create API key',
        quickstartTitle: 'Quickstart scaffold',
        quickstartDescription: 'Default tab follows onboarding build mode. Snippets are mocked placeholders for now.',
        recommendedTemplateTitle: 'Recommended template',
        recommendedTemplateDescription:
            'Placeholder area for template suggestions based on onboarding goals and build mode.',
        starterPipelineTitle: 'Starter content pipeline',
        starterPipelineDescription:
            'Suggested setup cards, starter prompts, and prefilled source wiring will render here.',
        applyTemplate: 'Apply template',
        consoleTitle: 'Console / response area',
        consoleDescription: 'Placeholder for SDK responses, request traces, and first successful output.',
        consoleStatus: 'Status: waiting for live backend integration…',
        consoleReady: 'Mock response panel ready'
    },
    home: {
        title: 'Home',
        unlimited: 'Unlimited',
        apiUsage: '// API usage',
        publishJobsPerDay: 'Publish jobs per day',
        last7Days: 'Last 7 days',
        jobsOnDate: '{{count}} publishing jobs on {{date}}',
        noJobsLast7Days: 'No publishing jobs in the last 7 days',
        usageUnavailable: 'Usage data is unavailable',
        postsUsed: '@{{account}} · {{used}} / {{limit}} posts used',
        resetsOn: 'resets {{date}}',
        noQuotaPeriod: 'No quota period',
        gettingStarted: '// Getting started',
        recentJobs: '// Recent jobs',
        loadingRecentJobs: 'Loading recent jobs…',
        recentJobsUnavailable: 'Recent jobs are unavailable.',
        noPublishingJobs: 'No publishing jobs yet.',
        untitledPost: 'Untitled post',
        activeKeyCount: '{{count}} active API key',
        loadingDescription: 'Loading your organization overview…',
        stats: {
            connectedAccounts: 'Connected accounts',
            sourcesInPlan: '{{count}} sources included in your plan',
            postsLeft: 'Posts left this period',
            postsAcrossAccounts: '{{count}} posts across connected accounts',
            scheduledJobs: 'Scheduled jobs'
        },
        checklist: {
            createOrganization: 'Create your organization',
            connectSocialAccount: 'Connect a social account',
            socialAccountDetail: 'Instagram or X',
            createApiKey: 'Create an API key',
            apiKeyScopes: 'publishing:read · publishing:publish',
            publishFirstPost: 'Publish your first post',
            publishEndpoint: 'POST /publishing/jobs'
        },
        actions: {
            createPost: 'Create post',
            start: 'Start',
            viewAll: 'View all',
            viewApiDocs: 'View API docs',
            createApiKey: 'Create API key',
            connectAccount: 'Connect an account',
            viewInsights: 'View insights',
            inviteMember: 'Invite a member'
        },
        quickActions: '// Quick actions',
        quickActionsTitle: 'Build with your organization',
        quickActionsDescription: 'Start with the tools already connected to your workspace.',
        status: { published: 'Published', processing: 'Processing', scheduled: 'Scheduled', other: 'Unknown' }
    },
    settings: {
        pageTitle: 'Settings',
        pageDescription: 'Manage your organization settings and preferences.',
        general: 'General',
        notifications: 'Notifications',
        danger: 'Danger Zone',
        billing: {
            tab: 'Billing',
            title: 'Billing',
            description: 'Review the current billing status for {{organizationName}}.',
            currentPlanEyebrow: '// current plan',
            entitlementsEyebrow: '// entitlements',
            noOrganizationTitle: 'No organization selected',
            noOrganizationDescription: 'Select or create an organization to view subscription and billing details.',
            portalFooter: 'Billing runs through Stripe. Manage cards and invoices in the customer portal.',
            lifetimeBadge: 'Lifetime',
            lifetimeFooter:
                'This organization is on a lifetime plan and is not billed through Stripe, so there are no cards or invoices to manage. Contact invisibleapi to change the plan.',
            cancelAtPeriodEndNotice: 'This subscription is set to cancel at the end of the current billing period.',
            manageBillingDisabledNoOrganization: 'Create or select an organization first.',
            manageBillingDisabledNoCustomer: 'This organization does not have a Stripe customer yet.',
            manageBillingDisabledLifetime:
                'A lifetime plan is not billed through Stripe, so it has no customer portal.',
            priceAfterTrialValue: '{{price}}/{{interval}}',
            postsLimitValue: '{{count}} posts per connected account per period.',
            noActivePlanTitle: 'No active plan',
            capabilitiesTitle: 'Capabilities',
            limitsTitle: 'Limits',
            emptyBillingState: 'No billing state is available for the selected organization yet.',
            emptyCapabilities: 'No capability data returned.',
            emptyLimits: 'No limit data returned.',
            fields: {
                subscriptionStatus: 'Subscription status',
                billingInterval: 'Billing interval',
                quantity: 'Sources (quantity)',
                priceAfterTrial: 'Price after trial',
                trialEndsAt: 'Trial ends',
                currentPeriod: 'Current period',
                accessPeriod: 'Access period',
                cancelAtPeriodEnd: 'Cancel at period end'
            },
            subscriptionStatuses: {
                active: 'Active',
                trialing: 'Trialing',
                incomplete: 'Incomplete',
                incomplete_expired: 'Incomplete expired',
                past_due: 'Past due',
                canceled: 'Canceled',
                unpaid: 'Unpaid',
                paused: 'Paused'
            },
            billingIntervals: {
                monthly: 'Monthly',
                yearly: 'Yearly'
            },
            boolean: {
                yes: 'Yes',
                no: 'No'
            },
            errors: {
                generic: 'Billing data could not be loaded.'
            },
            checkout: {
                loading: 'Redirecting…'
            },
            customerPortal: {
                open: 'Manage billing',
                loading: 'Opening portal…'
            },
            returnState: {
                success: 'Billing checkout completed. Refreshing billing state…',
                cancel: 'Billing checkout was canceled.',
                portalReturn: 'Returned from billing portal. Refreshing billing state…'
            }
        },
        generalTitle: 'General Settings',
        generalDescription: 'Configure your organization name and basic preferences.',
        workspaceName: 'Organization Name',
        workspaceSlug: 'Organization Slug',
        timezone: 'Timezone',
        settingsSaved: 'Settings saved',
        notificationsSaved: 'Notification preferences saved',
        notificationsTitle: 'Notification Preferences',
        notificationsDescription: 'Choose how and when you want to be notified.',
        emailNotifications: 'Email Notifications',
        emailNotificationsDescription: 'Receive email updates about organization activity.',
        pushNotifications: 'Push Notifications',
        pushNotificationsDescription: 'Get notified in your browser for important events.',
        weeklyDigest: 'Weekly Digest',
        weeklyDigestDescription: 'Receive a weekly summary of organization activity.',
        dangerTitle: 'Danger Zone',
        dangerDescription: 'Irreversible actions that affect your organization.',
        deleteWorkspace: 'Delete Workspace',
        deleteWorkspaceDescription: 'Permanently delete this organization and all its data.',
        deleteUnavailable: 'This action is not available in the template.',
        exportData: 'Export Data',
        exportDataDescription: 'Download a full export of your organization data.',
        exportStarted: 'Export started — you will be notified when ready.',
        savePreferences: 'Save Preferences'
    },
    accountDetails: {
        pageTitle: 'Account details',
        pageDescription: 'Manage your profile details and account security actions.',
        loadErrorTitle: 'Account details could not be loaded',
        retry: 'Retry',
        managedBadge: 'Centrally managed',
        readOnlyBadge: 'Read-only',
        emptyValue: 'Not set',
        fields: {
            firstName: 'First name',
            lastName: 'Last name',
            username: 'Username',
            email: 'Email'
        },
        personalInfo: {
            title: 'Personal information',
            description: 'Update the profile fields that are editable for this account.',
            sourceHint: 'Username and email come from your sign-in identity and cannot be edited here.',
            saving: 'Saving...',
            reset: 'Reset changes',
            saveSuccessToast: 'Account details saved.',
            validationErrorToast: 'Review the highlighted fields and try again.'
        },
        security: {
            title: 'Authentication & security',
            description:
                'Start account security flows through backend actions. Admin access never reaches the browser.',
            emailFlowBadge: 'Secure email flow',
            password: {
                title: 'Basic authentication',
                description:
                    'Send yourself the password update email and finish the change from the secure email flow.',
                methodLabel: 'Password update method',
                methodValue: 'Secure email flow',
                action: 'Send password update email',
                sending: 'Sending email...',
                successToast: 'Password update email requested. Check your inbox to continue.',
                successInline: 'Email requested. Check your inbox for the password update flow.'
            },
            twoFactor: {
                title: 'Two-factor authentication',
                description: 'Send yourself the setup email and configure an authenticator app from that flow.',
                methodLabel: 'Two-factor setup method',
                methodValue: 'Secure email flow',
                action: 'Send two-factor setup email',
                sending: 'Sending email...',
                successToast: 'Two-factor setup email requested. Check your inbox to continue.',
                successInline: 'Email requested. Check your inbox for the two-factor setup flow.'
            }
        }
    },
    sidebar: {
        appName: 'SaaS App',
        workspace: 'Organization',
        login: 'Login',
        logout: 'Logout',
        signedIn: 'Signed in',
        openSignIn: 'Open sign-in',
        account: 'Account',
        accountDetails: 'Account details',
        organizationMenu: 'Organizations',
        organizations: 'Organizations',
        currentOrganization: 'Current',
        noOrganization: 'No organization yet',
        language: 'Language',
        languageSelector: 'Choose language',
        languages: {
            en: 'English',
            de: 'German',
            es: 'Spanish',
            fr: 'French',
            mk: 'Macedonian',
            jp: 'Japanese'
        }
    },
    nav: {
        home: 'Home',
        organizations: 'Organizations',
        socialAccounts: 'Social Accounts',
        publishing: 'Publishing',
        insights: 'Insights',
        members: 'Members',
        invitations: 'Invitations',
        apiKeys: 'API Keys',
        apiDocs: 'API Docs',
        settings: 'Settings',
        pricing: 'Pricing',
        content: 'Content',
        audience: 'Audience',
        advancedQuery: 'Advanced query',
        main: 'Main'
    },
    publishing: {
        pageTitle: 'Publishing',
        pageDescription: 'Review publish jobs for {{organizationName}} and inspect the latest delivery state.',
        emptyOrganizationDescription: 'Select or create an organization to start reviewing publish jobs.',
        empty: 'No publish jobs found yet.',
        refreshAction: 'Reload jobs',
        refreshDescription: 'Refetch the latest publish jobs from the server.',
        createAction: 'Create post',
        subscriptionInactiveDescription:
            "Publishing new posts and retrying failed jobs is paused until this organization's subscription is renewed.",
        deliveryRecordsTitle: 'What delivery records mean',
        deliveryRecordsDescription:
            'Each delivery record is one platform-specific delivery attempt or result for a selected social account. Use it to inspect what happened per target.',
        scheduledFor: 'Scheduled for {{value}}',
        pagination: {
            previous: 'Previous',
            next: 'Next',
            page: 'Page {{page}}',
            summary: 'Page {{page}} · showing {{count}} jobs (up to {{perPage}} per page)',
            summaryCompact: '{{count}} jobs · Page {{page}} of {{totalPages}}'
        },
        emptyStateTitle: 'No publish jobs yet',
        previewAction: 'Preview',
        previewPostAction: 'Preview post',
        actionsMenuLabel: 'Open actions menu',
        technicalDetailsAction: 'Technical details',
        technicalDetailsTitle: 'Publish job technical details',
        technicalDetailsDescription: 'Inspect the raw publishing payload and delivery details for this job.',
        previewTitle: 'Publish job preview',
        previewDescription: 'Inspect the latest stored state for this publish job.',
        previewLoading: 'Loading publish job details…',
        instagram: {
            follow: 'Follow',
            mediaAlt: 'Instagram publish preview media',
            multiAsset: '{{count}} items',
            commentPlaceholder: 'Add a comment…',
            justNow: 'just now'
        },
        noCaption: 'No caption provided.',
        noMediaItems: 'No media items were attached to this publish job.',
        noDeliveries: 'No delivery records are available for this publish job yet.',
        failureTitle: 'Failure details',
        mediaItemsCount: '{{count}} media items',
        deliveriesCount: '{{count}} delivery records',
        columns: {
            status: 'Status',
            caption: 'Caption',
            targets: 'Deliveries',
            createdAt: 'Created',
            publishedAt: 'Published',
            actions: 'Actions',
            actionsDescription: 'Open the preview/details view for that publish job.'
        },
        fields: {
            status: 'Status',
            createdAt: 'Created at',
            updatedAt: 'Updated at',
            publishedAt: 'Published at',
            caption: 'Caption',
            firstComment: 'First comment',
            requestedPublishAt: 'Scheduled for',
            mediaItems: 'Media items',
            captionHelp: 'Keep it concise and platform-ready. You can review everything before submitting.',
            firstCommentHelp: 'Optional. This is posted after the main content if supported by the target platform.',
            mediaType: 'Media type',
            sourceUrl: 'Source URL',
            publicFetchUrl: 'Public fetch URL',
            altText: 'Alt text',
            deliveries: 'Deliveries',
            failureCode: 'Failure code',
            failureMessage: 'Failure message'
        },
        status: {
            pending: 'Pending',
            queued: 'Queued',
            scheduled: 'Scheduled',
            processing: 'Processing',
            published: 'Published',
            failed: 'Failed',
            canceled: 'Canceled'
        }
    },

    publishingComposer: {
        pageTitle: 'Create post',
        pageDescription: 'Compose a publish job for {{organizationName}} and submit it to the publishing workflow.',
        backToList: 'Back to publishing',
        emptyOrganizationTitle: 'No organization selected',
        emptyOrganizationDescription: 'Select or create an organization before composing a publish job.',
        publishTimeHint: 'Pick whether this post should go out immediately or at a scheduled local time.',
        localTimezoneHint: 'The scheduled time uses your current local timezone.',
        connectAccountsAction: 'Open social accounts',
        platformHint:
            'Choose where this post should be published. Instagram and X can publish when an active connected account is available.',
        socialAccountHint: 'Select the connected social account that should receive this post.',
        socialAccountAutoSelected: 'Your only eligible connected account has been pre-selected automatically.',
        selectSocialAccountAction: 'Select a social account',
        selectedAccountsSummary: '{{count}} selected accounts',
        selectedPlatformsLabel: 'Will publish on',
        limits: {
            checking: 'Checking publishing usage…',
            remaining: '{{count}} posts left this period',
            exhausted: 'Limit reached for this period',
            exhaustedWithReset: 'Limit reached. Resets {{resetAt}}',
            selectedExhaustedWarning: '{{account}} has no publishing allowance left. It resets {{resetAt}}.',
            selectedExhaustedWarningNoReset: '{{account}} has no publishing allowance left.',
            unavailable:
                'Publishing usage is unavailable right now. You can still publish; backend limits will be checked on submit.'
        },
        noEligibleAccountsTitle: 'No publishing accounts available',
        noEligibleAccountsDescription: 'Connect an Instagram or X account before creating a post.',
        noHandle: 'No public handle available',
        selectedBadge: 'Selected',
        notSelectedBadge: 'Not selected',
        comingSoon: 'Coming soon',
        noAccountsBadge: 'No account',
        additionalMediaItemsTitle: 'Additional media items',
        additionalMediaItemsDescription: 'Kept for carousel posts, X posting, and videos.',
        mediaPublicUrlHint: 'Media is passed as a public URL.',
        mediaStepHint: 'Add the media details and caption for this post.',
        textOnlyHint: 'Text-only X posts submit the caption without media items.',
        instagramNoImageHint: "If you don't upload an image, this post won't be published on Instagram.",
        mediaItemsTitle: 'Media items',
        mediaItemsHint: 'Add one or more images or videos when this post should include media.',
        multiMediaBadge: '{{count}} media items',
        addMediaAction: 'Add media to post',
        removeMediaAction: 'Remove',
        mediaItemTitle: 'Media item {{index}}',
        publicFetchUrlMirrorsSourceHint:
            'Backend requires source URL. This field is reused for both source URL and public fetch URL when we submit the post.',
        altTextPlaceholder: 'Accessible description for this media item',
        captionPlaceholder: 'Write the publish caption…',
        submitAction: 'Publish post',
        scheduleSubmitAction: 'Schedule post',
        subscriptionInactiveDescription: "Renew this organization's subscription before creating a new publish job.",
        stepCompleteBadge: 'Done',
        submitting: 'Publishing...',
        successToast: 'Publish job created.',
        successToastScheduled: 'Publish job scheduled.',
        livePreviewTitle: 'Live preview',
        livePreviewDescription: 'Preview the post while you compose, or open it fullscreen.',
        fullscreenPreviewAction: 'Fullscreen',
        selectDateAction: 'Select a date',
        previewAccountPlaceholder: 'Selected social account',
        previewHandlePlaceholder: '@account',
        previewMediaPlaceholder: 'Media preview',
        previewCaptionPlaceholder: 'Your caption will appear here as you type.',
        previewAdditionalMediaCount: '+{{count}} more',
        previewScheduledBadge: 'Scheduled',
        videoPreviewLabel: 'Video preview',
        twitterPreviewComingSoon: 'Twitter/X preview uses the selected X account and post content.',
        postPublishingTitle: 'Post publishing',
        stepFlowTitle: 'Step-by-step composer',
        stepFlowDescription: 'Complete each block in order. Later blocks stay locked until the previous one is ready.',
        publishModes: {
            now: 'Publish now',
            nowDescription: 'Start the publishing workflow immediately after submission.',
            scheduled: 'Schedule for later',
            scheduledDescription: 'Queue this post for a future date and time.'
        },
        platforms: {
            instagram: 'Instagram',
            twitter: 'Twitter / X'
        },
        steps: {
            socialAccounts: 'Social accounts',
            targets: 'Targets',
            publishingTime: 'Publishing time',
            postContent: 'Post content'
        },
        fields: {
            publishTime: 'Publishing time',
            publishAtLocalTime: 'Publish at (local time)',
            platform: 'Target platform',
            socialAccount: 'Social account',
            mediaItems: 'Media items',
            mediaUrl: 'Media URL',
            captionHelp: 'Keep it concise and platform-ready.',
            mediaType: 'Media type',
            sourceUrl: 'Source URL',
            publicFetchUrl: 'Public fetch URL',
            altText: 'Alt text',
            caption: 'Caption',
            requestedPublishAt: 'Scheduled for',
            scheduleDate: 'Date',
            scheduleTime: 'Time'
        },
        placeholders: {
            primaryMediaUrl: 'https://cdn.yourapp.com/photo.jpg',
            additionalMediaUrl: 'https://cdn.example.com/post-image.jpg'
        },
        mediaTypes: {
            text: 'Text only',
            image: 'Image',
            video: 'Video'
        },
        validation: {
            generic: 'Review the publish form and fix the highlighted fields.',
            socialAccountRequired: 'Select a social account before continuing.',
            platformRequired: 'Select an available target platform.',
            mediaItemRequired: 'Add at least one media item for the selected platforms.',
            mediaTypeInvalid: 'Text-only posts are available for X only.',
            publishModeRequired: 'Choose a publishing time option before continuing.',
            captionRequired: 'Caption is required.',
            publicFetchUrlRequired: 'Public fetch URL is required.',
            publicFetchUrlInvalid: 'Public fetch URL must be a valid URL.',
            xCarouselImagesOnly: 'X carousel posts support images only. Publish a video to X as a single-media post.',
            xCarouselImageCount: 'X carousel posts support at most {{count}} images.',
            requestedPublishAtRequired: 'Choose a scheduled date and time.',
            requestedPublishAtInvalid: 'Scheduled date and time must be valid.',
            requestedPublishAtFuture: 'Scheduled date and time must be in the future.'
        }
    },

    insights: {
        pageTitle: 'Instagram Insights',
        pageDescription: 'Choose an account and date range. The page updates around those two filters.',
        emptyOrganizationDescription: 'Select or create an organization to view Instagram insights.',
        accountPickerLabel: 'Instagram account',
        accountPickerPlaceholder: 'Select an account',
        noInsightsAccounts:
            'No connected Instagram account supports insights yet. Connect an Instagram professional account to get started.',
        noInsightsAccountsHelp:
            'Connect an Instagram account with insights access to unlock overview metrics, audience demographics, and content breakdowns.',
        subscriptionInactiveTitle: 'Insights paused',
        subscriptionInactiveDescription:
            "Instagram insights updates are paused while this organization's subscription is inactive.",
        subscriptionInactiveHelp: 'Renew the subscription to pull fresh insights data again.',
        connectAccountCta: 'Go to social accounts',
        dateRangeLabel: 'Date range',
        dateRange: {
            last7Days: 'Last 7 days',
            last14Days: 'Last 14 days',
            last30Days: 'Last 30 days'
        },
        overviewTitle: 'Overview',
        tabs: {
            overview: 'Overview',
            content: 'Content breakdown',
            trend: 'Trend',
            breakdowns: 'Breakdowns',
            audience: 'Audience',
            advanced: 'Advanced query'
        },
        currentViewLabel: 'Current view',
        currentViewTitle: 'Instagram account insights',
        currentViewDescription:
            'Summary cards surface the headline numbers. Use the tabs below for trend, breakdown, audience, and raw query exploration.',
        trendTitle: 'Reach over time',
        trendTitleWithMetric: '{{metric}} over time',
        trendDescription: 'Unique accounts reached per day in the selected range.',
        trendTracking: 'Tracking {{metric}} across the selected range.',
        chartValueLabel: 'Value',
        overviewSectionPrimaryLabel: '// primary',
        overviewSectionEngagementLabel: '// engagement',
        quickSegmentTitle: 'Quick segment summary',
        quickSegmentDescription: 'A fast read on the strongest content breakdown for the active metric.',
        topSegment: 'Top segment',
        topAudience: 'Top audience',
        rankTop: 'Top',
        primaryMetricBadge: 'Primary',
        metricHelpLabel: '{{metric}} help',
        filtersBadge: 'Instagram insights',
        filtersTitle: 'Focus the dashboard',
        filtersDescription: 'Choose an account and date range. The page updates around those two filters.',
        filtersAction: 'Filters',
        filtersSheetTitle: 'Insights filters',
        filtersSheetDescription: 'Adjust the account and date range for the dashboard.',
        breakdownTitle: 'Metric breakdowns',
        breakdownDescription: 'Split a metric total by a supported dimension.',
        breakdownMetricLabel: 'Metric',
        breakdownDimensionLabel: 'Breakdown',
        breakdownColumns: {
            rank: 'Rank',
            dimension: 'Dimension',
            value: 'Value',
            share: 'Share'
        },
        breakdownTotal: 'Total: {{value}}',
        audienceTitle: 'Audience demographics',
        audienceDescription: 'Who follows and engages with this account.',
        audienceMetricLabel: 'Audience',
        audienceMetrics: {
            follower_demographics: 'Followers',
            engaged_audience_demographics: 'Engaged audience'
        },
        audienceBreakdownLabel: 'Group by',
        audienceBreakdowns: {
            country: 'Country',
            city: 'City',
            age: 'Age',
            gender: 'Gender'
        },
        audienceTimeframeLabel: 'Timeframe',
        audienceTimeframes: {
            this_week: 'This week',
            this_month: 'This month'
        },
        advancedTitle: 'Advanced query',
        advancedDescription: 'Send a custom query to the Instagram insights API and inspect the raw response.',
        advancedExplorerDescription:
            'The explorer is intentionally secondary. Use it for validating edge-case metrics, unsupported breakdowns, and raw API payloads.',
        advancedBadges: {
            rawApiQueryBuilder: 'Raw API query builder',
            debugFirst: 'Debug-first'
        },
        advancedMetricLabel: 'Metrics (comma-separated)',
        advancedMetricPlaceholder: 'reach',
        advancedPeriodLabel: 'Period',
        advancedMetricTypeLabel: 'Metric type',
        advancedBreakdownLabel: 'Breakdown',
        advancedBreakdownPlaceholder: 'media_product_type',
        advancedTimeframeLabel: 'Timeframe',
        advancedSinceLabel: 'Since',
        advancedUntilLabel: 'Until',
        advancedRun: 'Run query',
        advancedNone: 'None',
        advancedPeriods: {
            day: 'Day',
            week: 'Week',
            days_28: '28 days',
            lifetime: 'Lifetime'
        },
        advancedMetricTypes: {
            total_value: 'Total value',
            time_series: 'Time series'
        },
        rawResponseTitle: 'Raw response',
        metrics: {
            reach: 'Reach',
            accounts_engaged: 'Accounts engaged',
            total_interactions: 'Total interactions',
            likes: 'Likes',
            comments: 'Comments',
            shares: 'Shares',
            saves: 'Saves',
            views: 'Views',
            follows_and_unfollows: 'Follows and unfollows',
            profile_links_taps: 'Profile link taps'
        },
        metricDescriptions: {
            reach: 'Unique accounts that saw the account content in the selected range.',
            accounts_engaged: 'Unique accounts that interacted with the account content.',
            total_interactions: 'Combined interactions across supported engagement actions.',
            default: 'High-level account insight metric.'
        },
        breakdownDimensions: {
            REELS: 'Reels',
            POSTS: 'Posts',
            STORIES: 'Stories',
            CAROUSEL_CONTAINER: 'Carousel posts',
            FOLLOWER: 'Followers',
            NON_FOLLOWER: 'Non-followers',
            FOLLOWERS: 'Followers',
            NON_FOLLOWERS: 'Non-followers',
            MEDIA_PRODUCT_TYPE: 'Media product type',
            FOLLOW_TYPE: 'Follow type',
            CONTACT_BUTTON_TYPE: 'Contact button type',
            THIS_WEEK: 'This week',
            THIS_MONTH: 'This month',
            UNKNOWN: 'Unknown',
            M: 'Male',
            F: 'Female',
            U: 'Unknown'
        },
        noData: 'No data was returned for this query.',
        retry: 'Retry',
        unknownError: 'Unknown error'
    },
    apiDocs: {
        pageTitle: 'API Docs',
        pageDescription:
            'Quick-start guides, endpoint reference cards, and the embedded API documentation in one place.',
        openFullDocumentation: 'Open full documentation',
        subscriptionInactiveDescription:
            'Organization API keys are suspended while subscription access is inactive, so authenticated API requests for this organization will fail until billing is renewed.',
        shell: {
            defaultDocsLocaleLabel: 'Default',
            fallbackNotice:
                'No dedicated docs artifact exists for frontend language {{frontendLanguage}}, so the page fell back to {{docsLanguage}}.',
            organizationIdLabel: 'Active organization ID',
            organizationIdDescription:
                'Use this ID when an API endpoint requires the active organization path parameter.',
            copyOrganizationId: 'Copy organization ID',
            organizationIdCopied: 'Organization ID copied.',
            noActiveOrganizationId: 'No active organization selected.',
            iframeRegionLabel: 'API docs embed container',
            iframeTitle: 'Embedded API docs for {{language}}',
            loadErrorTitle: 'Could not load docs manifest',
            loadErrorDescription: 'The docs manifest could not be loaded: {{error}}',
            loadErrorFallback: 'Unknown docs manifest error.',
            noDocsTitle: 'No generated docs artifacts found',
            noDocsDescription: 'The docs manifest loaded, but no generated static docs pages were available to embed.'
        }
    },
    socialAccounts: {
        pageTitle: 'Social Accounts',
        loadingDescription: 'Loading social accounts...',
        pageDescription: 'Manage the connected social account surfaces for your organization.',
        subscriptionInactiveDescription:
            "Adding or reconnecting social accounts is paused until this organization's subscription is renewed.",
        sourceLimit: {
            title: 'Source limit reached',
            description:
                'This organization is using {{used}} of {{limit}} purchased sources. Buy more sources before connecting another account.',
            actionBlockedToast: 'Source limit reached. Buy more sources before connecting another social account.',
            checking: 'Checking source limit…',
            unavailable: 'Connecting new accounts is paused until the source limit can be checked again.',
            unavailableTitle: 'Source limit could not be verified',
            unavailableRetry: 'Retry check',
            manageBillingAction: 'Manage billing',
            ownerHint: 'Ask an organization owner or admin to buy more sources.'
        },
        searchPlaceholder: 'Search social accounts',
        rowsPerPage: 'Rows per page',
        empty: 'No social accounts found.',
        table: {
            title: 'Connected accounts',
            totalCount: '{{count}} total accounts'
        },
        columns: {
            id: 'ID',
            accountName: 'Account name',
            type: 'Type',
            status: 'Status',
            actions: 'Actions',
            actionsDescription: 'Open the preview/details view for that publish job.'
        },
        actions: {
            copyId: 'Copy ID',
            refresh: 'Reconnect',
            reconnect: 'Reconnect',
            delete: 'Disconnect'
        },
        types: {
            instagram: 'Instagram',
            x: 'X'
        },
        statuses: {
            active: 'Connected',
            degraded: 'Degraded',
            reauthorization_required: 'Reconnect required',
            disconnected: 'Disconnected',
            error: 'Error'
        },
        pagination: {
            previous: 'Previous',
            next: 'Next',
            page: 'Page {{page}} of {{totalPages}}',
            summary: 'Showing {{start}}-{{end}} of {{total}} accounts',
            summaryCompact_one: '{{count}} account · Page {{page}} of {{totalPages}}',
            summaryCompact_other: '{{count}} accounts · Page {{page}} of {{totalPages}}'
        },
        connectDialog: {
            trigger: 'Add new account',
            title: 'Connect social account',
            description: 'Choose a social source to connect.',
            liveBadge: 'Live',
            comingSoonLabel: '// coming soon',
            trustCopy: 'You authorize directly with the platform. We never see your password.',
            platforms: {
                instagram: {
                    name: 'Instagram',
                    requirement: 'Business or Creator account linked to a Facebook Page.'
                },
                x: {
                    name: 'X',
                    requirement: 'Any X account.'
                }
            },
            comingSoonPlatforms: {
                tiktok: 'TikTok',
                facebook: 'Facebook',
                linkedin: 'LinkedIn',
                youtube: 'YouTube'
            },
            connectInstagram: 'Connect Instagram account',
            connectX: 'Connect X account'
        },
        deleteDialog: {
            title: 'Disconnect social account',
            description: 'Disconnect "{{accountName}}" from this organization?',
            success: 'Social account disconnected'
        },
        callback: {
            success: 'Social account connected successfully',
            error: 'Social account connection failed'
        },
        emptyOrganizationTitle: 'No organization selected',
        emptyOrganizationDescription: 'Pick an organization before managing social accounts.',
        errors: {
            missingAuthorization: 'No manageable authorization was found for this social account.'
        },
        idCopied: 'Social account ID copied'
    },
    organizationMembers: {
        pageTitle: 'Organization members',
        pageDescription: 'Manage members for {{organizationName}}.',
        fallbackOrganizationName: 'the current organization',
        empty: 'No members found for this organization.',
        count: '{{count}} members · Page {{page}} of {{totalPages}}',
        permissionHint:
            'Your current organization role can view this page, but member management actions are disabled for your permission level.',
        genericMember: 'this member',
        roleUpdated: 'Member role updated',
        memberRemoved: 'Member removed',
        removeAction: 'Remove member',
        confirmRemoveTitle: 'Remove organization member',
        confirmRemoveDescription:
            'Remove {{memberName}} from this organization? This action cannot be undone from the UI.',
        columns: {
            member: 'Member',
            role: 'Role',
            joinedAt: 'Joined date',
            actions: 'Actions',
            actionsDescription: 'Open the preview/details view for that publish job.'
        },
        roles: {
            owner: 'Owner',
            admin: 'Admin',
            member: 'Member',
            viewer: 'Viewer'
        },
        errors: {
            forbidden: 'You are not allowed to perform that member action.',
            notFound: 'That member or organization could not be found.',
            conflict: 'That change violates organization membership rules.',
            generic: 'Member action failed.'
        }
    },
    organizationInvitations: {
        pageTitle: 'Organization invitations',
        pageDescription: 'Manage invitations for {{organizationName}}.',
        fallbackOrganizationName: 'the current organization',
        empty: 'No invitations found for this organization.',
        emptyDashedTitle: 'There are no pending invitations for this organization yet.',
        count: '{{count}} invitations · Page {{page}} of {{totalPages}}',
        permissionHint:
            'Your current organization role can view invitations, but creating or revoking invitations is disabled for your permission level.',
        inviteAction: 'Invite teammate',
        dialogTitle: 'Invite teammate',
        dialogDescription: 'Create an organization invitation and copy the one-time token.',
        fields: {
            email: 'Email',
            role: 'Role'
        },
        sendInvite: 'Send invite',
        inviting: 'Creating invite...',
        invitationCreated: 'Invitation created',
        invitationRevoked: 'Invitation revoked',
        revokeAction: 'Revoke invitation',
        tokenTitle: 'Invitation token',
        tokenDescription: 'This token is shown only once. Copy it now if you need to share or inspect it.',
        copyToken: 'Copy token',
        tokenCopied: 'Invitation token copied',
        confirmRevokeTitle: 'Revoke invitation',
        confirmRevokeDescription: 'Revoke the invitation for {{email}}?',
        columns: {
            email: 'Email',
            role: 'Role',
            status: 'Status',
            expiresAt: 'Expires',
            createdAt: 'Created',
            actions: 'Actions',
            actionsDescription: 'Actions for this invitation, such as revoking it.'
        },
        roles: {
            owner: 'Owner',
            admin: 'Admin',
            member: 'Member',
            viewer: 'Viewer'
        },
        statuses: {
            pending: 'Pending',
            accepted: 'Accepted',
            declined: 'Declined',
            revoked: 'Revoked',
            expired: 'Expired'
        },
        errors: {
            forbidden: 'You are not allowed to perform that invitation action.',
            notFound: 'That invitation or organization could not be found.',
            conflict: 'That invitation action violates invitation state rules.',
            generic: 'Invitation action failed.'
        }
    },
    organizationInvitationAcceptance: {
        pageTitle: 'Organization invitation',
        pageDescription:
            'Review the invitation details, sign in if needed, and choose whether to join the organization.',
        preparingAuth: 'Preparing authentication…',
        resolving: 'Resolving invitation...',
        resolvingDescription: 'Checking the invitation token and loading the invitation details.',
        invitationDetailsTitle: 'Invitation details',
        invitationDetailsDescription:
            'This invitation can only be accepted or declined by the signed-in invited account.',
        fields: {
            organization: 'Organization',
            role: 'Role',
            email: 'Invited email',
            status: 'Status'
        },
        unknownOrganization: 'Unknown organization',
        loginRequiredTitle: 'Sign in required',
        loginRequired: 'You need to sign in before you can respond to this invitation.',
        loginAction: 'Sign in to respond to invitation',
        registerAction: 'Create account to respond to invitation',
        acceptAction: 'Accept invitation',
        declineAction: 'Decline invitation',
        wrongAccountTitle: 'Wrong account signed in',
        wrongAccountDescription:
            'This invitation is for {{invitedEmail}}, but you are signed in as {{currentEmail}}. Sign out and sign back in with the invited account.',
        accepting: 'Accepting invitation...',
        acceptingDescription: 'We are joining you to the organization now.',
        declining: 'Declining invitation...',
        decliningDescription: 'We are declining this invitation now.',
        success: 'Invitation accepted successfully.',
        declinedSuccess: 'Invitation declined.',
        declinedSuccessDescription: 'The invitation has been declined. Taking you to your organization...',
        redirecting: 'Redirecting you into the organization...',
        errorTitle: 'Could not process invitation',
        errors: {
            missingToken: 'Invitation token is missing from the URL.',
            unauthorized: 'Please sign in and try the invitation again.',
            invalidToken: 'This invitation is invalid, expired, declined, revoked, or already used.',
            generic: 'Invitation action failed.'
        }
    },
    organizationApiKeys: {
        pageTitle: 'Organization API keys',
        pageDescription: 'Manage API keys for {{organizationName}}.',
        fallbackOrganizationName: 'the current organization',
        empty: 'No API keys found for this organization.',
        permissionHint:
            'Your current organization role can view API keys, but creating or revoking API keys is disabled for your permission level.',
        subscriptionInactiveDescription:
            "Existing API keys are suspended and new API keys cannot be created until this organization's subscription is renewed.",
        createAction: 'Create API key',
        dialogTitle: 'Create API key',
        dialogDescription: 'Create an organization API key and copy the token now — it will not be shown again.',
        fields: {
            name: 'Name',
            permissions: 'Permissions'
        },
        permissionsDescription: 'Choose which permissions this API key should be allowed to use.',
        permissionsPublishIncludesRead: 'Publish access also includes read access and will automatically add it.',
        permissionsInsightsSeparate:
            'Publishing permissions no longer include insights access. Existing keys with only publishing permissions cannot read insights - add the insights read permission when creating a key that needs it.',
        permissions: {
            publishing_read: {
                label: 'Read publishing data',
                description: 'Allows read-only access to publishing resources and published content.'
            },
            publishing_publish: {
                label: 'Publish media',
                description: 'Allows creating publishing jobs and uploading or publishing media.'
            },
            insights_read: {
                label: 'Read insights data',
                description: 'Allows read-only access to Instagram insights data.'
            }
        },
        previewAction: 'Preview API key',
        previewExistingAction: 'Preview details',
        backToEdit: 'Back to edit',
        previewTitle: 'Preview API key',
        previewDescription: 'Review the API key details and permissions before creating it.',
        resultTitle: 'API key created',
        resultDescription:
            'Copy and store the API key token securely, then review the saved key details and permissions.',
        previewSummaryTitle: 'API key preview',
        previewSummaryDescription: 'Confirm the key name and scopes before generating the token.',
        previewPermissionsDescription: 'These are the scopes that will be attached to the new API key.',
        previewExistingTitle: 'API key details',
        previewExistingDescription: 'Review the saved API key metadata and granted permissions.',
        previewExistingLoading: 'Loading API key details...',
        previewExistingEmpty: 'No API key details were returned for this key.',
        previewExistingEndpointUnavailable:
            'Detailed preview endpoint is not available yet, so this modal is showing the metadata already loaded from the API keys list.',
        previewExistingPermissionsDescription: 'These are the scopes currently granted to this API key.',
        previewExistingScopesLoaded: 'Permissions were loaded from the API key details endpoint.',
        previewExistingNoScopes: 'No scopes were returned for this API key.',
        previewExistingTokenAvailable: 'This API key token is available for display right now.',
        previewExistingTokenUnavailable:
            'This API key can be previewed, but the raw token is not available for display.',
        previewExistingLimitation: 'The token value is not available for this API key.',
        previewFields: {
            maskedKey: 'Masked key',
            fullKey: 'Full API key',
            revokedAt: 'Revoked at'
        },
        createSubmit: 'Create key',
        creating: 'Creating key...',
        apiKeyCreated: 'API key created',
        apiKeyRevoked: 'API key revoked',
        revokeAction: 'Revoke API key',
        tokenTitle: 'API key token',
        tokenDescription:
            'Copy and store this token securely. It may remain available in API key details while backend support allows it.',
        copyToken: 'Copy token',
        tokenCopied: 'API key token copied',
        confirmRevokeTitle: 'Revoke API key',
        confirmRevokeDescription: 'Revoke the API key "{{apiKeyName}}"?',
        columns: {
            name: 'Name',
            status: 'Status',
            createdAt: 'Created',
            actions: 'Actions',
            actionsDescription: 'Open the preview/details view for that publish job.'
        },
        statuses: {
            active: 'Active',
            revoked: 'Revoked',
            suspended: 'Suspended'
        },
        errors: {
            forbidden: 'You are not allowed to perform that API key action.',
            notFound: 'That API key or organization could not be found.',
            conflict: 'That API key action violates API key state rules.',
            generic: 'API key action failed.'
        }
    },
    organizations: {
        loading: {
            title: 'Loading organizations',
            description: 'Fetching your organizations and choosing the active context.'
        },
        error: {
            title: 'Could not load organizations',
            description: 'The organization-aware shell could not initialize.',
            fallback: 'Unknown organizations error'
        },
        subscriptionInactive: {
            title: 'Subscription inactive',
            actionBlockedToast: "Renew this organization's subscription before using this action.",
            manageBillingAction: 'Manage billing',
            ownerHint: 'Ask an organization owner to renew or reactivate billing.'
        },
        limitBanner: {
            cancelEndingTitle: 'Subscription ending soon',
            cancelEndingDescription:
                'This workspace is set to lose access at the end of the current billing period. Renew or update billing to avoid interruption.',
            subscriptionInactiveDescription:
                'This workspace has inactive billing access. Update the subscription to restore full access.',
            trialEndingTitle: 'Trial ending soon',
            trialEndingDescription:
                'Your trial is close to ending. Add billing details or upgrade now to avoid interruption.',
            manageBillingAction: 'Manage billing',
            socialAccountsReachedTitle: 'Social account limit reached',
            socialAccountsReachedDescription:
                'You are using {{used}} of {{limit}} connected social account slots. Upgrade to connect more accounts.',
            upgradeAction: 'Upgrade plan',
            socialAccountsWarningTitle: 'Approaching social account limit',
            socialAccountsWarningDescription:
                'You are using {{used}} of {{limit}} connected social account slots. Upgrade before new connections get blocked.',
            connectedAccountFallback: 'this connected account',
            publishingReachedTitle: 'Publishing quota reached',
            publishingReachedDescription:
                '{{accountName}} has used all {{limit}} posts for the current billing period. Upgrade to keep publishing.',
            publishingWarningTitle: 'Approaching publishing quota',
            publishingWarningDescription:
                '{{accountName}} has used {{currentUsage}} of {{limit}} posts for the current billing period. Upgrade before publishing gets blocked.'
        },
        fields: {
            name: 'Organization name',
            slug: 'Slug',
            slugHelp: 'Optional. Leave blank to auto-generate a slug from the organization name.'
        },
        create: {
            cta: 'Create organization',
            title: 'Create organization',
            description: 'Create a new organization and switch into it immediately.',
            pageTitle: 'New organization',
            pageDescription: 'Create a new organization and start managing it right away.',
            pageIntro: 'Set up a new organization',
            pageHint:
                'After creation, the new organization becomes the active context automatically and you are redirected back into the app.',
            sectionTitle: 'Create another organization',
            sectionDescription:
                'Create a new organization directly from the Organizations page and make it active immediately.',
            hideSection: 'Hide form',
            submit: 'Create',
            submitting: 'Creating...',
            success: 'Organization created',
            error: 'Failed to create organization',
            conflictError: 'An organization with that name or slug already exists.',
            validationFallback: 'Please fix the form errors and try again.'
        },
        switcher: {
            label: 'Organizations',
            selectOrganization: 'Select organization',
            loadMore: 'Load more organizations ({{shown}}/{{total}})',
            loadError: 'Could not load organizations.',
            emptyTitle: 'No organizations yet',
            emptyDescription: 'Create your first organization to unlock organization-scoped pages.'
        },
        emptyState: {
            title: 'Create your first organization',
            description:
                'You do not have an organization yet. Create one to initialize your organization context and unlock organization-aware pages.'
        },
        onboarding: {
            title: 'Organizations',
            description:
                'Create your first organization here. Once you have one, the rest of the app becomes available normally.',
            createTitle: 'Create your first organization',
            createDescription: 'Start here to establish the organization context the rest of the product depends on.',
            workspaceSetupTitle: 'Finish organization setup',
            workspaceSetupDescription:
                'Accounts without organizations can still use Organizations and Settings so you can finish setup and manage your profile.',
            nextStepsTitle: 'What happens next',
            nextStepsDescription: 'Once the first organization exists, the app can continue with the normal flow.',
            nextStepsCreate: 'Create the organization that will become your default context.',
            nextStepsInvite: 'After creation, you can come back here to invite teammates and expand the organization.',
            nextStepsUnlock:
                'Organization-scoped pages become available automatically after the first organization is created.'
        },
        pageShell: {
            title: 'Organizations',
            description: 'A dedicated home for organization context, creation, and upcoming management surfaces.',
            overview: {
                title: 'Organization',
                description: 'Review your current organizations and choose the active context for the rest of the app.',
                currentState: 'Active organization'
            },
            metrics: {
                totalOrganizations: 'Total organizations',
                activeOrganization: 'Active organization',
                activeRole: 'Active role',
                noneSelected: 'Not selected'
            },
            list: {
                count: '{{count}} organizations',
                title: 'Your organizations',
                description: 'See every organization you belong to and switch the active context from here.',
                switchAction: 'Make active',
                membershipIdLabel: 'Membership ID: {{membershipId}}'
            },
            quickActions: {
                title: 'Quick actions',
                description: 'Kick off organization flows from one place.',
                hint: 'This page will become the canonical destination for organization onboarding and management.'
            },
            tabs: {
                title: 'Organization sections',
                description: 'Switch between organization management areas for the active organization.',
                organizations: 'Organizations',
                members: 'Members',
                invitations: 'Invitations',
                membersDescription: 'Manage organization members for the active organization.',
                invitationsDescription: 'Manage organization invitations for the active organization.'
            },
            membersPlaceholder: {
                title: 'Members section placeholder',
                description:
                    'Member management will live here once the dedicated organizations page absorbs the current members flow.'
            },
            invitationsPlaceholder: {
                title: 'Invitations section placeholder',
                description:
                    'Invitation management will live here once the organizations page absorbs the current invitations flow.'
            },
            routingNotice: 'Members and invitations now live under Organizations. API keys remain separate for now.'
        }
    },
    confirmDialog: {
        loading: 'Loading...'
    },
    oauthConsent: {
        pageTitle: 'Authorize access',
        pageDescription: 'Review this request before granting access to your organization data.',
        clientRequestTitle: '{{clientName}} is requesting access',
        clientRequestDescription: 'Approving lets this app act on your behalf with the permissions listed below.',
        scopesTitle: 'Requested permissions',
        scopesEmpty: 'No specific permissions were requested.',
        scopes: {
            mcp: 'Full MCP access',
            mcpDescription: 'Publishing and insights tools through the MCP gateway.'
        },
        expiresAt: 'This request expires at {{date}}.',
        organizationTitle: 'Organization',
        organizationDescription: 'Choose which organization this app may access.',
        organizationSingleDescription: 'Access will be granted for this organization.',
        organizationsEmptyTitle: 'No organization available',
        organizationsEmptyDescription: 'You need at least one organization before you can approve this request.',
        approve: 'Approve',
        deny: 'Deny',
        approving: 'Approving...',
        denying: 'Denying...',
        redirecting: 'Redirecting…',
        redirectsTo: 'You will be redirected to {{url}}.',
        continueToClient: 'Continue to {{host}}',
        redirectBlocked: 'The redirect destination is not allowed, so you were not redirected.',
        errorTitle: 'This request cannot be completed',
        errors: {
            missingRequest: 'The consent request reference is missing from the URL.'
        }
    },

    onboardingFlow: {
        placeholderPreviewTitle: 'Preview',
        placeholderPreviewDescription: 'Step-specific preview content will render here.',
        stepPlaceholderDescription:
            'Implementation lands in the follow-up step PRs. This shell PR wires the routes, layout, shared state, and navigation only.',
        stepBadge: 'Step {{step}}',
        saveFailed: 'Could not save your onboarding progress. Please try again.',
        back: 'Back',
        continue: 'Continue',
        next: 'Next',
        pricePerMonth: '${{price}}/mo',
        finish: 'Finish',
        steps: {
            personalize: 'Personalize',
            connect: 'Connect',
            activate: 'Activate',
            personalizeLabel: '01 · Personalize',
            connectLabel: '02 · Connect'
        },
        shell: {
            breadcrumb: 'Onboarding',
            help: 'Open onboarding help',
            counter: 'onboarding · {{current}} / {{total}}'
        },
        personalize: {
            title: 'Personalize',
            description: 'Tell us what you’re building so we can tailor your onboarding and organization defaults.',
            badge: 'Step 01 · Personalize',
            heroTitle: 'What are you building?',
            goalsTitle: 'What do you want to do first?',
            goalsLabel: '// pick your goals',
            buildModeTitle: 'How do you want to build?',
            buildModeLabel: "// how you'll build",
            sourcesTitle: 'How many sources do you expect to connect?',
            sourcesLabel: '// connected sources',
            sourcesDescription: '2 sources are included. Additional sources are charged at $5/mo each.',
            sourcesHelper: '2 included, then $5/mo each.',
            templatesIncluded: '17 starter templates included on every plan',
            decreaseSources: 'Fewer sources',
            increaseSources: 'More sources',
            goalSingular: 'goal',
            goalPlural: 'goals',
            summary: '{{goalCount}} {{goalLabel}} · {{mode}} · {{sources}} sources · {{price}}/mo',
            previewEmpty: 'Pick one or more goals to preview the setup summary.',
            previewGoals: 'Selected goals',
            previewBuildMode: 'Build mode',
            previewSources: 'Sources declared: {{count}}',
            previewPrice: 'Estimated price: {{price}}',
            goals: {
                publish: {
                    label: 'Publish content',
                    description: 'Push content into channels with minimal glue code.',
                    cardTitle: 'Publish & schedule',
                    cardDescription: 'Reels, carousels, stories — on your schedule',
                    endpointChip: 'POST /v1/publish/post',
                    recipeTitle: 'Content scheduler',
                    recipeDescription: 'Queue and auto-publish reels and carousels from your own app or DB.'
                },
                comments: {
                    label: 'Moderate comments',
                    description: 'Automate replies, triage, and moderation flows.',
                    cardTitle: 'Comments & mentions',
                    cardDescription: 'Pull conversations the moment they happen',
                    endpointChip: 'GET /v1/read/comments',
                    recipeTitle: 'Mention listener',
                    recipeDescription: 'Webhook fires when someone mentions you — pipe it to Slack or your CRM.'
                },
                analytics: {
                    label: 'Read analytics',
                    description: 'Pull account and content metrics into your app.',
                    cardTitle: 'Analytics & insights',
                    cardDescription: 'Reach, growth, best time to post',
                    endpointChip: 'GET /v1/read/analytics',
                    recipeTitle: 'Analytics dashboard',
                    recipeDescription: 'Reach, growth and best-time metrics, computed for you, into your DB.'
                },
                ai: {
                    label: 'Ship AI agents',
                    description: 'Combine content tools with agent-driven workflows.',
                    cardTitle: 'AI comment replies',
                    cardDescription: 'Draft replies in your tone — approve or auto-send',
                    endpointChip: 'MCP · reply tools',
                    recipeTitle: 'Comment reply assistant',
                    recipeDescription: 'An AI agent drafts replies in your brand tone; you approve or auto-send.'
                },
                ugc: {
                    label: 'Handle UGC',
                    description: 'Collect and route creator content with approvals.',
                    cardTitle: 'Repost UGC',
                    cardDescription: 'Find tagged content and repost it',
                    endpointChip: 'GET /v1/read/mentions',
                    recipeTitle: 'UGC repost tool',
                    recipeDescription: 'Find tagged posts, request rights, repost — the whole loop automated.'
                },
                webhooks: {
                    label: 'Listen to webhooks',
                    description: 'React to account and publishing lifecycle events.',
                    cardTitle: 'Webhooks & events',
                    cardDescription: 'Signed webhooks for comments, mentions, DMs',
                    endpointChip: 'POST /v1/webhooks',
                    recipeTitle: 'Event relay',
                    recipeDescription: 'Signed, retried webhooks for every comment, mention and DM.'
                }
            },
            buildModes: {
                code: {
                    label: 'Writing code',
                    description: 'I want full API control and direct implementation.',
                    summary: 'REST API & SDKs'
                },
                agent: {
                    label: 'With an AI agent',
                    description: 'I want an agent to help wire and automate the setup.',
                    summary: 'MCP for Claude & Cursor'
                },
                both: {
                    label: 'Both',
                    description: 'I want code-first control with agent assistance when useful.',
                    summary: 'Code + agents'
                }
            }
        },
        connect: {
            title: 'Connect Instagram',
            description:
                'Connect at least one professional Instagram account now, or skip and go straight to trial checkout.',
            badge: 'Step 02 · Connect',
            headingTitle: 'Connect',
            headingEmphasis: 'Connect Instagram.',
            headingRest: 'One professional account now, or skip to checkout.',
            requirementsLabel: '// requirements',
            skip: 'Skip for now',
            afterTrial: '/mo after trial',
            trialBadge: '7-day trial',
            completedBadge: 'Checkout completed',
            connectLaterBadge: 'Connect later',
            priceStripBreakdown: '{{sources}} sources · 2 included in the $9 base',
            priceStripTerms: '7-day trial · card on day 0 · first charge day 8',
            trust: {
                secureTitle: 'Secure OAuth only',
                secureDescription: 'We never ask for your Instagram password. You authorize access directly with Meta.',
                setupTitle: 'Posting-ready setup',
                setupDescription:
                    'We use this connection to prepare scheduling, publishing, and analytics for your organization.',
                laterTitle: 'You can add more later',
                laterDescription: 'Connect one account now, then add more accounts whenever your team is ready.'
            },
            requirementsTitle: 'Requirements before you connect',
            requirements: {
                professional: 'Your Instagram account must be a professional Business or Creator account.',
                page: 'That Instagram account must already be linked to a Facebook Page.'
            },
            connectionTitle: 'Instagram via Meta',
            connectionDescription:
                'Authorize with Instagram via Meta — you’ll be redirected and brought right back here.',
            connectedCounter: 'connected: {{connected}} / {{sources}}',
            declaredSources: 'Declared sources: {{count}}',
            connected: 'Connected: {{count}}',
            syncedSources: 'Synced sources: {{count}}',
            errorTitle: 'Connection failed',
            errors: {
                callbackFailed: 'Connecting your Instagram account failed. Please try again.',
                popupBlocked: 'Instagram login popup was blocked. Enable popups and try again.',
                oauthDenied: 'Instagram authorization was canceled before permissions were granted.',
                personalAccount:
                    'Only Instagram professional accounts are supported. Switch this account to Business or Creator first.',
                missingPage: 'This Instagram account must be connected to a Facebook Page before it can be used here.'
            },
            ctaConnect: 'Connect Instagram account',
            ctaConnectAnother: 'Connect another account',
            ctaConnectShort: 'Connect account',
            ctaConnected: 'Connected',
            ctaWaiting: 'Waiting for Instagram…',
            previewTitle: 'Connected accounts',
            previewDescription: 'Your connected account count can raise the source count automatically.',
            previewSourcesNow: 'Sources now: {{count}}',
            previewEmptyTitle: 'No Instagram accounts connected yet',
            previewEmptyDescription: 'Connect an account on the left to preview how your onboarding setup fills in.',
            previewNextStep: 'Next step',
            previewActivate: 'Activate free trial',
            previewRequirementsLink: 'Review Instagram requirements',
            previewSyncedMessage: 'Shared state keeps sources synced upward. Current declared sources: {{count}}',
            checkoutCanceled: 'Checkout was canceled. Your onboarding state is still here.',
            checkoutUnavailable:
                'Checkout isn’t available yet — no billing product is configured. Please try again later.',
            finalizingTitle: 'Finalizing your subscription',
            finalizingDescription: 'Waiting for Stripe to confirm your checkout. This usually takes a few seconds.',
            checkoutTitle: 'Trial pricing',
            checkoutDescription: 'There is no separate Step 3 anymore — checkout starts directly from here.',
            checkoutCta: 'Start {{days}}-day free trial',
            nextToDashboard: 'Go to dashboard welcome state',
            previewConnectLater: 'Connect later from the dashboard if needed.',
            previewTrialDetailsTitle: 'Trial details',
            previewTrialDetails:
                'Day 0: card collected. Day 8: first charge. Success redirects into the dashboard welcome state.'
        },
        activate: {
            title: 'Activate your organization',
            description:
                'Start your free trial, confirm the source count, and hand the setup off to your dashboard welcome state.',
            trialActivatedTitle: 'Trial activated',
            trialActivatedDescription:
                'Your organization is ready. The dashboard welcome state should pick up your goals, connection status, and {{count}}-source plan.',
            waitingCards: {
                connectionTitle: 'Instagram connection',
                connectionDescription:
                    'If you skipped connect, you can finish it from the dashboard welcome checklist.',
                workspaceTitle: 'Organization finishing touches',
                workspaceDescription:
                    'Your sources, goals, and activation summary carry into the post-checkout welcome state.'
            },
            nextToDashboard: 'Go to dashboard welcome state',
            startTrial: 'Start {{days}}-day free trial',
            cancelBannerTitle: 'Checkout canceled',
            cancelBannerDescription: 'Your trial was not started. Everything you set up here is still preserved.',
            badge: 'Step 3 · Activate',
            heroTitle: 'Activate your {{days}}-day free trial',
            heroDescription:
                'No real billing yet. This is a mocked Stripe handoff that preserves your onboarding state through redirect, cancel, and success.',
            postTrialPrice: 'Post-trial price',
            chargedStarting: 'Charged starting day {{day}}',
            trust: {
                startToday: 'Start your {{days}}-day free trial today',
                cardRequired: 'Card required to activate your organization',
                noCharge: 'You will not be charged until day {{day}}'
            },
            priceBreakdownTitle: 'Price breakdown',
            priceBreakdownDescription:
                'Adjust sources inline before starting the trial. Pricing stays locked to the shared helper.',
            sourcesTitle: 'Sources',
            sourcesDescription: 'Use at least {{count}} sources for this setup.',
            baseLabel: 'Base ({{count}} sources)',
            baseDetail: 'Included before add-ons',
            additionalLabel: 'Additional sources',
            additionalDetailNone: 'No extra sources selected',
            additionalDetailSome: '{{count}} × ${{price}}',
            monthlyAfterTrialLabel: 'Monthly after trial',
            monthlyAfterTrialDetail: 'Exact price billed after the free trial',
            breakdownIncluded: '{{count}} sources included in the ${{price}} base',
            breakdownExpanded: '${{base}} base + {{extraCount}} × ${{extraPrice}}',
            stripeHandoffTitle: 'Mock Stripe handoff',
            stripeHandoffDescription:
                'Use the mocked states below to preview checkout redirect, cancel, and success without touching real billing.',
            stripeReady: 'Ready to launch',
            stripeRedirecting: 'Redirecting to Stripe…',
            redirectTitle: 'Redirecting to Stripe Checkout',
            redirectDescription:
                'In production, this is where Stripe would collect the card and return to the app. For now, choose the mocked result.',
            simulateCancel: 'Simulate cancel return',
            simulateSuccess: 'Simulate successful checkout',
            stripeCtaNote:
                'The CTA starts a mocked Stripe session and keeps your onboarding state intact across the return path.',
            recapTitle: 'Setup recap',
            recapHeading: 'Ready to activate',
            recapGoals: 'Goals selected',
            recapInstagram: 'Instagram',
            recapSources: 'Sources',
            recapTrialDetails: 'Trial details',
            recapAfterCheckout: 'After checkout',
            recapNoneYet: 'None yet',
            recapConnected: '{{count}} connected',
            recapSkipped: 'Not yet — skipped for now',
            recapNotConnected: 'Not connected yet',
            recapSourcesValue: '{{count}} sources · {{price}} after trial',
            recapTrialValue: '{{days}} days free · card required · no charge until day {{day}}',
            recapAfterCheckoutPending:
                'The dashboard welcome state receives this setup summary after checkout succeeds.',
            recapAfterCheckoutComplete: 'Send the user into the dashboard welcome state with this setup summary.',
            expectedPriceTitle: 'Expected monthly price after trial'
        }
    },
    pricing: {
        pageTitle: 'Pricing',
        pageDescription: 'Clear source-based pricing that fits the rest of the app experience.',
        eyebrow: 'Pricing',
        calculatorTitle: 'Calculate your pricing',
        calculatorDescription: 'Choose how many sources you need and the monthly total updates instantly.',
        accountsLabel: 'Sources',
        accountsIncludedLabel: 'Sources included in your plan',
        minAccounts: '{{count}} source',
        maxAccounts: '{{count}} sources',
        firstTierLabel: 'First {{count}} sources',
        firstTierRate: '{{price}} for {{count}} sources',
        additionalAccountsLabel: 'Additional sources',
        additionalAccountsRate: '{{price}} per additional source',
        monthlyTotalLabel: 'Monthly total',
        monthlyOnlyHint: 'Live estimate, billed monthly',
        customSolutionTitle: 'Need a custom solution?',
        customSolutionDescription: 'Talk to us if you need higher-volume pricing or onboarding help.',
        trialTitle: '7-day trial',
        trialDescription: 'Start with a 7-day trial, then continue on the same monthly quantity-based plan.',
        checkoutCta: 'Start trial in Stripe',
        requiresOrganization: 'Create or select an organization before starting checkout.',
        checkoutForOrganization: 'Checkout will start for {{organizationName}} with the selected quantity.',
        manageSubscriptionCta: 'Manage subscription',
        alreadySubscribed:
            '{{organizationName}} already has an active subscription. Change the number of sources from Settings.',
        contactCta: 'Contact us',
        planTitle: 'Your plan',
        planDescription:
            'Transparent monthly pricing with {{count}} sources included, then a flat add-on per extra source.',
        month: 'month',
        accountsSelected: '{{count}} sources selected',
        planFirstTierLine: 'First {{count}} sources included',
        planTierCalculation: '{{price}} × {{count}}',
        includedLabel: 'Included',
        primaryCta: 'Get started',
        monthlyOnlyDisclaimer: 'Monthly pricing only. No credits or annual billing assumptions.',
        features: {
            organizations: 'Organizations, members, and invitations',
            socialAccounts: 'Connected social accounts',
            publishing: 'Post publishing workflows',
            insights: 'Instagram insights',
            apiKeys: 'Organization API keys',
            customerSupport: 'Customer support'
        }
    },
    apiErrors: {
        auth: {
            required: 'Authentication is required.',
            invalidOrExpiredToken: 'Authentication is required. Your session may be invalid or expired.'
        },
        validation: {
            invalidRequestBody: 'The request is invalid. Please review the submitted data and try again.',
            required: 'This field is required.',
            emailInvalid: 'Please enter a valid email address.',
            stringMaxLength: 'This value is too long.'
        },
        organizations: {
            invalidRequest: 'The organization request is invalid.',
            conflict: 'An organization with these details already exists.',
            notFound: 'The organization could not be found.',
            loadFailed: 'Organizations could not be loaded.',
            createFailed: 'The organization could not be created.',
            validation: {
                nameMaxLength: 'The organization name is too long.',
                slugMaxLength: 'The organization slug is too long.',
                slugFormat: 'The organization slug may contain only lowercase letters, numbers, and hyphens.'
            }
        },
        onboarding: {
            updateForbidden: 'Only organization owners and admins can update onboarding.',
            validation: {
                goalsRequiredBeforeConnect: 'Select at least one onboarding goal before continuing to connect.'
            }
        },
        billing: {
            invalidRequest: 'The billing request is invalid.',
            manageForbidden: 'You are not allowed to manage billing for this organization.',
            organizationNotFound: 'The organization could not be found.',
            organizationOrProductNotFound: 'The organization or billing product could not be found.',
            incompleteConfiguration: 'Billing is not configured completely for this checkout flow yet.',
            lifetimeSubscriptionActive:
                'This organization is on a lifetime plan, so Stripe billing is unavailable. Contact invisibleapi to change the plan.',
            integration: 'The billing provider returned an integration error.',
            productsLoadFailed: 'Billing plans could not be loaded.',
            stateLoadFailed: 'The organization billing state could not be loaded.',
            checkoutFailed: 'The checkout session could not be created.',
            customerPortalFailed: 'The customer portal could not be opened.',
            validation: {
                successUrlInvalid: 'The billing success URL is invalid.',
                cancelUrlInvalid: 'The billing cancel URL is invalid.',
                returnUrlInvalid: 'The billing return URL is invalid.'
            }
        },
        userProfile: {
            loadFailed: 'Account details could not be loaded.',
            updateFailed: 'Account details could not be saved.',
            actionEmailFailed: 'The account security email could not be requested.',
            accountServiceUnavailable: 'The account service could not complete the request right now.',
            notFound: 'The account profile could not be found.',
            updateRejected: 'The account service rejected the account details update.',
            actionRejected: 'The account service rejected the security email request.',
            validation: {
                readOnly: 'This field is read-only.',
                unsupported: 'This field is not supported.'
            }
        },
        organizationInvitations: {
            listForbidden: 'You are not allowed to view invitations for this organization.',
            createForbidden: 'You are not allowed to invite members to this organization.',
            revokeForbidden: 'You are not allowed to revoke invitations for this organization.',
            createConflict: 'This invitation cannot be created because it violates organization invitation rules.',
            revokeConflict: 'This invitation cannot be revoked because of its current state.',
            notFound: 'The organization or invitation could not be found.',
            invalidToken: 'This invitation is invalid, expired, revoked, or already used.',
            actionFailed: 'The invitation action could not be completed.'
        },
        organizationMembers: {
            listForbidden: 'You are not allowed to view members for this organization.',
            removeForbidden: 'You are not allowed to remove this member.',
            removeConflict: 'This member cannot be removed because it violates organization membership rules.',
            updateForbidden: 'You are not allowed to update this member.',
            updateConflict: 'This member role change violates organization membership rules.',
            actionFailed: 'The member action could not be completed.',
            validation: {
                roleInvalid: 'The selected organization role is invalid.'
            }
        },
        organizationApiKeys: {
            listForbidden: 'You are not allowed to view API keys for this organization.',
            manageForbidden: 'You are not allowed to manage API keys for this organization.',
            notFound: 'The organization or API key could not be found.',
            createConflict: 'This API key cannot be created because it violates organization API key rules.',
            revokeConflict: 'This API key cannot be revoked because of its current state.',
            actionFailed: 'The API key action could not be completed.',
            validation: {
                nameMaxLength: 'The API key name is too long.'
            }
        },
        socialAccounts: {
            listFailed: 'Social accounts could not be loaded.',
            connectFailed: 'The social account connection could not be started.',
            reconnectFailed: 'The social account reconnect flow could not be started.',
            disconnectFailed: 'The social account could not be disconnected.',
            forbidden: 'You are not allowed to manage social accounts for this organization.',
            notFound: 'The organization or social account could not be found.',
            conflict: 'This social account action could not be completed because of its current state.'
        },
        publishing: {
            listFailed: 'Publish jobs could not be loaded.',
            detailFailed: 'The publish job details could not be loaded.',
            createFailed: 'The publish job could not be created.',
            paidPlanLimitExceeded: 'A paid plan publishing limit has been reached.',
            paidPlanLimitExceededForAccount: 'Publishing limit reached for {{account}}.',
            paidPlanLimitExceededUnknownAccount: 'the selected account',
            paidPlanLimitExceededUsageDetails:
                'Current usage: {{currentUsage}}/{{limit}}. Active reservations: {{activeReservations}}. Requested posts: {{requestedCount}}.',
            paidPlanLimitExceededResetDetails: 'Try again after {{resetAt}}.',
            forbidden: 'You are not allowed to manage publishing for this organization.',
            notFound: 'The organization or publish job could not be found.',
            conflict: 'This publish request could not be completed because of current publishing constraints.',
            validation: {
                mediaUrlUnreachable:
                    'The media URL could not be reached. Provide a publicly accessible direct link to a media file.',
                mediaUrlHttpError: 'The media URL responded with an error instead of serving a media file.',
                mediaUrlNotImage:
                    'This URL does not point to an image file. Link directly to an image (for example a .jpg URL), not a web page.',
                mediaUrlNotVideo:
                    'This URL does not point to a video file. Link directly to a video (for example an .mp4 URL), not a web page.'
            }
        },
        oauthConsent: {
            loadFailed: 'The consent request could not be loaded.',
            approveFailed: 'The consent request could not be approved.',
            denyFailed: 'The consent request could not be denied.',
            invalidRequest: 'The consent request is invalid.',
            forbidden: 'You are not allowed to approve this request for the selected organization.',
            notFound: 'This consent request is unknown, expired, or already handled.'
        },
        insights: {
            loadFailed: 'Insights could not be loaded.',
            invalidQuery: 'The insights query is invalid. Review the metric, period, and parameter combination.',
            forbidden: 'You are not allowed to view insights for this organization.',
            notFound: 'The organization or social account could not be found.',
            notReadable: 'This account cannot provide insights right now. Reconnect it with the insights permission.',
            providerRejected:
                'Instagram rejected this insights request. The requested metric combination may not be supported for this account.'
        }
    },
    validation: {}
} as const;
